import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API_BASE } from '../config';
import { useTranslation } from '../translations';
import {
  PanelContainer, Title, Subtitle, PageHeader, PanelCard, SectionTitle,
  FieldLabel, RequiredMark, TextInput, TextArea, LinkButton,
  PrimaryButton, SecondaryButton, IconButton, TableWrap, TitleRow,
} from '../ui/common';
import { XIcon, ArrowLeftIcon } from '../ui/icons';
import { Banner } from '../components/ui/Banner';
import SimpleSelect from '../ui/SimpleSelect';
import NipLookupInput from '../components/company/NipLookupInput';
import { formatMoney, formatPercent } from '../utils/format';
import { docTypeInfo } from '../utils/invoiceStatus';
import {
  round2, calcItem, cenaBrutto, zmienPozycje, pozycjaZPodpowiedzi, pozycjaZFaktury, pozycjaDoZapisu,
  kluczStawki, zmienStawke, stawkaZKlucza, KLUCZE_STAWEK, KOD_ZWOLNIENIA,
} from '../utils/invoiceItems';

const DOC_TYPE_VALUES = ['VAT', 'PROFORMA', 'SIMPLIFIED'];
const PAYMENT_METHOD_VALUES = ['transfer', 'cash', 'card', 'blik', 'other'];
// Najwięcej znaków podstawy zwolnienia: tyle mieści P_19A w KSeF (lustro lib/vatRate.js).
const MAKS_PODSTAWY = 256;

/**
 * Błąd stawek i podstawy zwolnienia przed zapisem (klucz tłumaczenia) albo null. Lustro reguł
 * serwera z lib/vatRate.js (F.15): firma zwolniona wystawia tylko pozycje „zw”, pozycja „zw”
 * wymaga podstawy zwolnienia, „…” z podpowiedzi art. 43 to niewpisany numer punktu.
 */
export function bladStawekFaktury(items, { zwolniona, podstawa }) {
  if (zwolniona && items.some((it) => kluczStawki(it) !== KOD_ZWOLNIENIA)) return 'invoices.editor.validation.exemptTaxedItem';
  if (!items.some((it) => kluczStawki(it) === KOD_ZWOLNIENIA)) return null;
  const tekst = String(podstawa || '').trim();
  if (!tekst) return 'invoices.editor.validation.exemptionBasisRequired';
  if (/…|\.\.\./.test(tekst)) return 'invoices.editor.validation.exemptionBasisIncomplete';
  return null;
}

// ── Helpers ────────────────────────────────────────────────────────────────
// Daty jako dzień KALENDARZOWY w strefie przeglądarki. toISOString() liczy w UTC, więc między
// 0:00 a 2:00 „dziś" było wczoraj, a data z bazy („2026-09-18T22:00:00.000Z" to 19 września
// w Polsce) po obcięciu do 10 znaków cofała się o dzień — edycja proformy przesuwała jej daty.
const pad2 = (n) => String(n).padStart(2, '0');
const toYmd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayIso = () => toYmd(new Date());
const addDays = (iso, days) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  return toYmd(new Date(y, m - 1, d + days));
};
const dateOnly = (value) => {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : toYmd(d);
};

// ── Styles ─────────────────────────────────────────────────────────────────
// Uklad: formularz po lewej, przyklejone podsumowanie z akcjami po prawej — ten sam szkielet co
// podglad faktury (dokument + szyna „Rozliczenie"). Do tej pory suma i przyciski staly na samym
// dole dlugiego formularza, wiec przy edycji pozycji kwota do zaplaty byla poza ekranem.

// PanelContainer przycina zawartosc (overflow: hidden), a przyciety przodek wylacza sticky
// u potomkow (patrz components/document/builderChrome.js). Nic tu nie wystaje poza kontener
// poza lista SimpleSelect, ktora i tak renderuje sie w portalu.
const Page = styled(PanelContainer)`
  overflow: visible;
`;
const EditorLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: var(--sp-6);
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Main = styled.div`
  min-width: 0;
  & > :last-child { margin-bottom: 0; }
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--sp-4);
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  ${p => p.full && 'grid-column: 1 / -1;'}
`;

const PartiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--sp-6);

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const PartyHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--sp-2);
  margin-bottom: var(--sp-2);
  font-size: var(--fs-sm);

  ${FieldLabel} { margin: 0; }
`;
// Sprzedawca to ZAMROZONY odczyt z Ustawien, nie pole do edycji — stad cichsze tlo panelu
// i obrys, a nie wyglad inputu. Wczesniej mial tlo --input-bg, czyli biale na bialej karcie:
// wygladal jak blok tekstu zawieszony w pustce.
const SellerBox = styled.div`
  padding: var(--sp-4);
  border: 1px solid var(--border);
  border-radius: var(--r-md);
  background: var(--panel-bg);
  font-size: var(--fs-sm);
  line-height: 1.6;
  color: var(--text-muted);
  overflow-wrap: anywhere;

  strong { display: block; font-size: var(--fs-base); color: var(--text); }
`;
const SellerAccount = styled.div`
  margin-top: var(--sp-2);
  padding-top: var(--sp-2);
  border-top: 1px dashed var(--border);
  font-size: var(--fs-xs);
  font-variant-numeric: tabular-nums;
`;
const SelectedClient = styled.div`
  display: flex;
  align-items: baseline;
  gap: var(--sp-2);
  margin-bottom: var(--sp-3);
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;
const RadioGroup = styled.div`
  display: flex;
  gap: var(--sp-4);
  flex-wrap: wrap;
  margin-bottom: var(--sp-4);

  label {
    display: inline-flex;
    align-items: center;
    gap: var(--sp-2);
    font-size: var(--fs-base);
    cursor: pointer;
  }
  input { accent-color: var(--primary); }
`;

// Pola w tabeli pozycji: ten sam jezyk co TextInput (obrys, fokus), tylko nizsze — w wierszu
// tabeli pole 48 px wysokosci rozpychaloby tabele do dwoch ekranow.
const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 820px;

  th, td { padding: var(--sp-2) var(--sp-3); vertical-align: middle; }
  th {
    text-align: left;
    font-size: var(--fs-xs);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
    color: var(--text-muted);
    background: var(--card-bg);
    border-bottom: 1px solid var(--input-border);
  }
  td { border-bottom: 1px solid var(--border); font-size: var(--fs-sm); }
  tbody tr:last-child td { border-bottom: none; }
  th.num, td.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
  td.lp { color: var(--text-muted); font-variant-numeric: tabular-nums; }

  input {
    width: 100%;
    box-sizing: border-box;
    padding: var(--sp-2) var(--sp-3);
    font-family: inherit;
    font-size: var(--fs-sm);
    border: 1px solid var(--input-border);
    border-radius: var(--r-sm);
    background: var(--input-bg);
    color: var(--text);
    transition: border-color var(--dur) var(--ease), box-shadow var(--dur) var(--ease);

    &:focus { outline: none; border-color: var(--primary); box-shadow: var(--focus-ring); }
  }
  td.num input { text-align: right; font-variant-numeric: tabular-nums; }
`;
const BasisField = styled(Field)`
  margin-top: var(--sp-4);
`;
const ItemsFooter = styled.div`
  margin-top: var(--sp-3);
`;
const ItemsHint = styled.p`
  margin: var(--sp-3) 0 0;
  font-size: 0.85rem;
  color: var(--text-muted);
`;
const NotesField = styled(Field)`
  margin-top: var(--sp-6);
`;

// Odnosnik-przycisk WEWNATRZ Banner: dziedziczy kolor tonu przez currentColor i jest zawsze
// podkreslony, bo w banerze nie ma innego sygnalu, ze to akcja.
const BannerLinkButton = styled(LinkButton).attrs({ $tone: 'inherit' })`
  text-decoration: underline;
  font-weight: 700;
`;

// ── Podsumowanie (szyna) ───────────────────────────────────────────────────
const Rail = styled.aside`
  @media (min-width: 1101px) {
    position: sticky;
    top: var(--sp-6);
  }
`;
const SummaryCard = styled(PanelCard)`
  margin-bottom: 0;
  padding: var(--sp-5);
`;
const SummaryEyebrow = styled.div`
  margin-bottom: var(--sp-1);
  font-size: var(--fs-xs);
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-muted);
`;
const SummaryRows = styled.dl`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: var(--sp-2) var(--sp-3);
  margin: 0;
  font-size: var(--fs-sm);
  font-variant-numeric: tabular-nums;

  dt { color: var(--text-muted); }
  dd { margin: 0; text-align: right; }
`;
const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: wrap;
  gap: var(--sp-1) var(--sp-3);
  margin-top: var(--sp-4);
  padding-top: var(--sp-4);
  border-top: 1px solid var(--border);

  span { font-size: var(--fs-sm); font-weight: 600; color: var(--text-muted); }
  strong {
    font-size: 1.5rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: var(--text);
  }
`;
const RailActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  margin-top: var(--sp-5);

  & > button { width: 100%; }
`;

// ── ClientPicker (autocomplete) ────────────────────────────────────────────
const PickerWrap = styled.div`
  position: relative;
`;
const PickerList = styled.div`
  position: absolute;
  top: 100%; left: 0; right: 0;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  margin-top: var(--sp-1);
  max-height: 220px;
  overflow-y: auto;
  z-index: var(--z-dropdown);
  box-shadow: var(--shadow-md);
`;
const PickerOption = styled.div`
  padding: var(--sp-3);
  cursor: pointer;
  font-size: var(--fs-base);
  border-bottom: 1px solid var(--border);
  &:hover { background: var(--hover-bg); }
  &:last-child { border-bottom: none; }
  small { color: var(--text-muted); font-size: var(--fs-xs); display: block; margin-top: 2px; }
`;

function ClientPicker({ value, onSelect, placeholder }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/clients?q=${encodeURIComponent(query)}&limit=10`, {
          credentials: 'include'
        });
        if (res.ok) {
          const data = await res.json();
          setResults(Array.isArray(data) ? data : []);
        }
      } catch (_) { setResults([]); }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  return (
    <PickerWrap>
      <TextInput
        type="text"
        value={value ? `${value.firstname || value.company_name || ''} ${value.lastname || ''}`.trim() : query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); onSelect(null); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        placeholder={placeholder}
      />
      {open && results.length > 0 && (
        <PickerList>
          {results.map(c => (
            <PickerOption key={c.id} onClick={() => { onSelect(c); setOpen(false); setQuery(''); }}>
              <div>
                <strong>{c.client_type === 'company' && c.company_name ? c.company_name : `${c.firstname || ''} ${c.lastname || ''}`}</strong>
              </div>
              <small>
                {c.email}{c.phone_number ? ` · ${c.phone_number}` : ''}
                {c.client_type === 'company' && c.company_nip ? ` · NIP: ${c.company_nip}` : ''}
              </small>
            </PickerOption>
          ))}
        </PickerList>
      )}
    </PickerWrap>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function InvoiceEditor({ mode = 'create' }) {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const [searchParams] = useSearchParams();
  const reservationParam = searchParams.get('reservation');
  const { t, currentLanguage } = useTranslation();

  const [docType, setDocType] = useState('VAT');
  const [issueDate, setIssueDate] = useState(todayIso());
  const [saleDate, setSaleDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState(addDays(todayIso(), 7));
  const [paymentMethod, setPaymentMethod] = useState('transfer');
  const [notes, setNotes] = useState('');

  const [seller, setSeller] = useState(null);
  const [client, setClient] = useState(null);
  const [buyer, setBuyer] = useState({
    buyer_type: 'private',
    buyer_name: '',
    buyer_nip: '',
    buyer_regon: '',
    buyer_address: '',
    buyer_email: '',
    buyer_phone: ''
  });

  const [items, setItems] = useState([
    { name: '', unit: 'szt.', quantity: 1, unit_price_net: 0, vat_rate: 23 }
  ]);

  // Stawka nowej pozycji jako klucz listy ('23', '0', 'zw'); firma zwolniona dostaje 'zw' (F.15).
  const [defaultRateKey, setDefaultRateKey] = useState('23');
  const [vatExempt, setVatExempt] = useState(false);
  const [exemptionBasis, setExemptionBasis] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [reservation, setReservation] = useState(null);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const [csRes, invSettRes] = await Promise.all([
          fetch(`${API_BASE}/api/contract-settings`, { credentials: 'include' }),
          fetch(`${API_BASE}/api/invoice-settings`, { credentials: 'include' })
        ]);

        if (csRes.ok) {
          const cs = await csRes.json();
          if (cs.settings) {
            const s = cs.settings;
            const street = s.company_street || '';
            const postal = s.company_postal_code || '';
            const city = s.company_city || '';
            const addr = [street, [postal, city].filter(Boolean).join(' ')].filter(Boolean).join(', ') || s.company_address || '';
            setSeller({
              name: s.company_name || '',
              nip: s.company_nip,
              regon: s.company_regon,
              krs: s.company_krs,
              address: addr,
              email: s.company_email,
              phone: s.company_phone,
              bank_account: s.company_bank_account,
              bank_name: s.company_bank_name
            });
          }
        }

        if (invSettRes.ok) {
          const invs = await invSettRes.json();
          if (invs.settings) {
            const zwolniona = invs.settings.vat_status === 'exempt';
            setVatExempt(zwolniona);
            if (invs.settings.vat_exemption_basis) setExemptionBasis(invs.settings.vat_exemption_basis);
            // Stawka domyślna spoza listy (zapisana przed F.14) nie przechodzi na pozycje: serwer
            // i tak by ją odrzucił, więc zostaje 23%.
            const klucz = zwolniona ? KOD_ZWOLNIENIA : kluczStawki({ vat_rate: invs.settings.default_vat_rate });
            if (KLUCZE_STAWEK.includes(klucz)) {
              setDefaultRateKey(klucz);
              setItems(prev => prev.map(it => zmienStawke(it, klucz)));
            }
            if (invs.settings.default_payment_method) setPaymentMethod(invs.settings.default_payment_method);
            const term = Number(invs.settings.default_payment_term_days);
            if (Number.isFinite(term)) setDueDate(addDays(todayIso(), term));
          }
        }

        // From reservation — używamy nowego bezpiecznego endpointu który filtruje po organization_id
        if (reservationParam) {
          const r = await fetch(`${API_BASE}/api/invoices/prefill/reservation/${reservationParam}`, { credentials: 'include' });
          if (r.ok) {
            const prefill = await r.json();
            setReservation(prefill.reservation);
            if (prefill.client) {
              setClient(prefill.client);
              populateBuyerFromClient(prefill.client);
            }
            if (Array.isArray(prefill.items) && prefill.items.length > 0) {
              // Pozycje z rezerwacji liczą się od brutto (`unit_price_gross`, utils/invoiceItems.js).
              setItems(prefill.items.map(it => pozycjaZPodpowiedzi(it, 23)));
            }
            // F.10: forma płatności z wpłat rezerwacji (np. karta z terminala) zamiast domyślnej.
            if (PAYMENT_METHOD_VALUES.includes(prefill.payment_method)) setPaymentMethod(prefill.payment_method);
          } else if (r.status === 404) {
            setError(t('invoices.editor.reservationNotFound'));
          }
        }

        // Edit mode
        if (mode === 'edit' && editId) {
          const eRes = await fetch(`${API_BASE}/api/invoices/${editId}`, { credentials: 'include' });
          if (eRes.ok) {
            const { invoice, items: invItems } = await eRes.json();
            setDocType(invoice.doc_type);
            setIssueDate(dateOnly(invoice.issue_date) || todayIso());
            setSaleDate(dateOnly(invoice.sale_date) || todayIso());
            if (invoice.payment_due_date) setDueDate(dateOnly(invoice.payment_due_date));
            setPaymentMethod(invoice.payment_method || 'transfer');
            setNotes(invoice.notes || '');
            setBuyer({
              buyer_type: invoice.buyer_type || 'private',
              buyer_name: invoice.buyer_name || '',
              buyer_nip: invoice.buyer_nip || '',
              buyer_regon: invoice.buyer_regon || '',
              buyer_address: invoice.buyer_address || '',
              buyer_email: invoice.buyer_email || '',
              buyer_phone: invoice.buyer_phone || ''
            });
            setItems((invItems || []).map(pozycjaZFaktury));
            if (invoice.vat_exemption_basis) setExemptionBasis(invoice.vat_exemption_basis);
          }
        }
      } catch (e) {
        setError(t('invoices.editor.loadError'));
      } finally {
        setLoading(false);
      }
    })();
    /* eslint-disable-next-line */
  }, []);

  function populateBuyerFromClient(c) {
    const isCompany = c.client_type === 'company';
    const streetPart = [c.street, c.building_number].filter(Boolean).join(' ') + (c.apartment_number ? `/${c.apartment_number}` : '');
    const cityPart = [c.postal_code, c.city].filter(Boolean).join(' ');
    const composed = [streetPart.trim(), cityPart.trim()].filter(Boolean).join(', ');
    setBuyer({
      buyer_type: isCompany ? 'company' : 'private',
      buyer_name: isCompany ? (c.company_name || `${c.firstname} ${c.lastname}`.trim()) : `${c.firstname || ''} ${c.lastname || ''}`.trim(),
      buyer_nip: isCompany ? (c.company_nip || '') : '',
      buyer_regon: isCompany ? (c.company_regon || '') : '',
      buyer_address: composed || c.address || '',
      buyer_email: c.email || '',
      buyer_phone: c.phone_number || ''
    });
  }

  // Items handlers
  const updateItem = useCallback((idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? zmienPozycje(it, field, value) : it));
  }, []);
  const updateRate = useCallback((idx, klucz) => {
    setItems(prev => prev.map((it, i) => i === idx ? zmienStawke(it, klucz) : it));
  }, []);

  const addItem = () => {
    setItems(prev => [...prev, { name: '', unit: 'szt.', quantity: 1, unit_price_net: 0, ...stawkaZKlucza(defaultRateKey) }]);
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  // Totals
  const totals = useMemo(() => {
    const enriched = items.map(it => calcItem(it));
    const total_net = round2(enriched.reduce((s, it) => s + it.total_net, 0));
    const total_vat = round2(enriched.reduce((s, it) => s + it.total_vat, 0));
    const total_gross = round2(total_net + total_vat);
    // Po kluczu stawki, nie po liczbie: 0% i „zw” to osobne wiersze (lustro rozbicieVat serwera).
    const breakdownMap = new Map();
    for (const it of enriched) {
      const key = kluczStawki(it);
      const prev = breakdownMap.get(key) || { key, rate: Number(it.vat_rate) || 0, net: 0, vat: 0, gross: 0 };
      prev.net = round2(prev.net + it.total_net);
      prev.vat = round2(prev.vat + it.total_vat);
      prev.gross = round2(prev.gross + it.total_gross);
      breakdownMap.set(key, prev);
    }
    return {
      items: enriched,
      total_net, total_vat, total_gross,
      breakdown: Array.from(breakdownMap.values())
        .sort((a, b) => (b.rate - a.rate) || ((a.key === KOD_ZWOLNIENIA) - (b.key === KOD_ZWOLNIENIA)))
    };
  }, [items]);

  const updateBuyer = (field, value) => setBuyer(prev => ({ ...prev, [field]: value }));
  // Dane z rejestru (GUS albo wykaz VAT) nadpisują nazwę, REGON i adres: użytkownik kliknął
  // „Pobierz dane” właśnie po to. NIP zapisujemy w postaci z rejestru (same cyfry).
  const fillBuyerFromRegistry = (c) => setBuyer(prev => ({
    ...prev,
    buyer_type: 'company',
    buyer_nip: c.nip || prev.buyer_nip,
    buyer_name: c.name || prev.buyer_name,
    buyer_regon: c.regon || prev.buyer_regon,
    buyer_address: c.address || prev.buyer_address,
  }));

  const maZwolnione = items.some((it) => kluczStawki(it) === KOD_ZWOLNIENIA);

  // Faktura VAT/uproszczona bez NIP-u albo adresu sprzedawcy nie jest poprawnym dokumentem.
  const sellerIncomplete = !seller || !String(seller.nip || '').trim() || !String(seller.address || '').trim();

  const validate = () => {
    // Serwer i tak odrzuci (seller_data_missing), ale tu użytkownik dostaje wskazówkę, zanim
    // straci wpisane pozycje na nieudanym zapisie.
    if (docType !== 'PROFORMA' && sellerIncomplete) return t('invoices.editor.validation.sellerRequired');
    if (!buyer.buyer_name.trim()) return t('invoices.editor.validation.buyerNameRequired');
    if (docType === 'VAT' && buyer.buyer_type === 'company' && !buyer.buyer_nip.trim()) return t('invoices.editor.validation.nipRequired');
    if (items.length === 0) return t('invoices.editor.validation.itemsRequired');
    for (const it of items) {
      if (!it.name.trim()) return t('invoices.editor.validation.itemNameRequired');
    }
    const bladVat = bladStawekFaktury(items, { zwolniona: vatExempt, podstawa: exemptionBasis });
    if (bladVat) return t(bladVat);
    return null;
  };

  const save = async (asDraft = false) => {
    const err = validate();
    if (err) { setError(err); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setSaving(true);
    setError(null);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const payload = {
        doc_type: docType,
        reservation_id: reservation?.id || null,
        client_id: client?.id || null,
        buyer,
        items: items.map(pozycjaDoZapisu),
        // Podstawa zwolnienia tylko przy pozycji „zw”; serwer zamraża ją na fakturze (F.15).
        vat_exemption_basis: maZwolnione ? exemptionBasis.trim() : null,
        issue_date: issueDate,
        sale_date: saleDate,
        payment_due_date: dueDate,
        payment_method: paymentMethod,
        notes,
        save_as_draft: asDraft
      };

      const url = mode === 'edit' ? `${API_BASE}/api/invoices/${editId}` : `${API_BASE}/api/invoices`;
      const method = mode === 'edit' ? 'PUT' : 'POST';
      const body = mode === 'edit' ? { ...payload, publish: !asDraft } : payload;
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('invoices.editor.saveError'));

      const newId = mode === 'edit' ? Number(editId) : data.invoice?.id;
      navigate(`/invoices/${newId}`);
    } catch (e) {
      setError(e.message || t('invoices.editor.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PanelContainer>
        <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{t('common.loading')}</p>
      </PanelContainer>
    );
  }

  const money = (value) => formatMoney(value, { locale: currentLanguage });
  // Faktura firmy zwolnionej (same pozycje „zw”): bez kolumn VAT, tytuł „Faktura” (F.15).
  const bezVat = items.length > 0 && items.every((it) => kluczStawki(it) === KOD_ZWOLNIENIA);
  const etykietaStawki = (klucz) => (klucz === KOD_ZWOLNIENIA
    ? t('invoices.editor.vatExemptCode')
    : formatPercent(Number(klucz), { locale: currentLanguage, decimals: 0 }));
  // Firma zwolniona wybiera tylko „zw”; stawka spoza listy (pozycja proformy sprzed zmiany statusu)
  // zostaje widoczna, żeby było jasne, co trzeba poprawić.
  const opcjeStawki = (it) => {
    const klucze = vatExempt ? [KOD_ZWOLNIENIA] : KLUCZE_STAWEK;
    const obecny = kluczStawki(it);
    return [...klucze, ...(klucze.includes(obecny) ? [] : [obecny])]
      .map((k) => ({ value: k, label: etykietaStawki(k) }));
  };
  const noBankAccount = paymentMethod === 'transfer' && !(seller && String(seller.bank_account || '').trim());
  const fakturaBezVat = vatExempt && docType === 'VAT';
  const issueLabel = docType === 'PROFORMA'
    ? t('invoices.editor.issueProforma')
    : fakturaBezVat
      ? t('invoices.editor.issueExempt')
      : (t(`invoices.editor.issueByType.${docType}`) || t('invoices.editor.issueGeneric'));
  const docTypeLabelKey = fakturaBezVat ? 'invoices.docType.vatExempt' : docTypeInfo(docType).labelKey;

  return (
    <Page>
      <PageHeader>
        <TitleRow>
          <IconButton onClick={() => navigate('/invoices')} aria-label={t('invoices.editor.backToList')}>
            <ArrowLeftIcon />
          </IconButton>
          <div>
            <Title>{mode === 'edit' ? t('invoices.editor.titleEdit') : t('invoices.editor.titleNew')}</Title>
            {reservation?.id && (
              <Subtitle>{t('invoices.editor.fromReservation', { id: reservation.id })}</Subtitle>
            )}
          </div>
        </TitleRow>
      </PageHeader>

      {error && <Banner tone="danger" style={{ marginBottom: 'var(--sp-4)' }}>{error}</Banner>}

      <EditorLayout>
        <Main>
          {/* Nagłówek dokumentu */}
          <PanelCard>
            <SectionTitle>{t('invoices.editor.sections.header')}</SectionTitle>
            <Grid>
              <Field>
                <FieldLabel>{t('invoices.filters.docType')}</FieldLabel>
                <SimpleSelect
                  style={{ width: '100%' }}
                  data-cy="invoice-doc-type"
                  aria-label={t('invoices.filters.docType')}
                  value={docType}
                  onChange={(val) => setDocType(val)}
                  disabled={mode === 'edit'}
                  options={DOC_TYPE_VALUES.map(v => ({
                    value: v,
                    label: t(v === 'VAT' && vatExempt ? 'invoices.docType.vatExempt' : docTypeInfo(v).labelKey),
                  }))}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="invoice-issue-date">{t('invoices.fields.issueDate')}</FieldLabel>
                <TextInput id="invoice-issue-date" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
              </Field>
              <Field>
                <FieldLabel htmlFor="invoice-sale-date">{t('invoices.fields.saleDate')}</FieldLabel>
                <TextInput id="invoice-sale-date" type="date" value={saleDate} onChange={(e) => setSaleDate(e.target.value)} />
              </Field>
              <Field>
                <FieldLabel htmlFor="invoice-due-date">{t('invoices.fields.dueDate')}</FieldLabel>
                <TextInput id="invoice-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              </Field>
              <Field>
                <FieldLabel>{t('invoices.fields.paymentMethod')}</FieldLabel>
                <SimpleSelect
                  style={{ width: '100%' }}
                  aria-label={t('invoices.fields.paymentMethod')}
                  value={paymentMethod}
                  onChange={(val) => setPaymentMethod(val)}
                  options={PAYMENT_METHOD_VALUES.map(v => ({ value: v, label: t(`invoices.paymentMethod.${v}`) }))}
                />
              </Field>
            </Grid>

            {/* U.13 (FAK-06): przelew wymaga numeru konta firmy — inaczej backend odrzuci wystawienie.
                `.trim()` spójne z backendem: same białe znaki liczą się jako brak konta. */}
            {noBankAccount && (
              <Banner tone="warning" compact style={{ marginTop: 'var(--sp-4)' }} data-cy="invoice-bank-account-hint">
                {t('invoices.editor.bankAccountMissing')}{' '}
                <BannerLinkButton onClick={() => navigate('/contract-settings')}>
                  {t('invoices.editor.completeCompanyData')}
                </BannerLinkButton>{' '}{t('invoices.editor.orChooseOtherMethod')}
              </Banner>
            )}
          </PanelCard>

          {/* Sprzedawca i Nabywca */}
          <PanelCard>
            <SectionTitle>{t('invoices.editor.sections.parties')}</SectionTitle>
            <PartiesGrid>
              <div>
                <PartyHead>
                  <FieldLabel as="span">{t('invoices.fields.seller')}</FieldLabel>
                  {seller && (
                    <LinkButton onClick={() => navigate('/contract-settings')}>
                      {t('invoices.editor.editCompanyData')}
                    </LinkButton>
                  )}
                </PartyHead>
                {seller ? (
                  <SellerBox>
                    <strong>{seller.name}</strong>
                    <div>{seller.address}</div>
                    {seller.nip && <div>{t('invoices.nipLabel', { nip: seller.nip })}</div>}
                    {seller.email && <div>{seller.email}</div>}
                    {seller.bank_account && (
                      <SellerAccount>{t('invoices.editor.bankAccountLabel', { account: seller.bank_account })}</SellerAccount>
                    )}
                    {sellerIncomplete && docType !== 'PROFORMA' && (
                      <Banner tone="warning">{t('invoices.editor.validation.sellerRequired')}</Banner>
                    )}
                  </SellerBox>
                ) : (
                  <Banner tone="danger">{t('invoices.editor.sellerMissing')}</Banner>
                )}
              </div>

              <div>
                <PartyHead>
                  <FieldLabel as="span">{t('invoices.fields.buyer')}</FieldLabel>
                </PartyHead>
                {!client && mode !== 'edit' && (
                  <div style={{ marginBottom: 'var(--sp-3)' }}>
                    <ClientPicker
                      value={null}
                      onSelect={(c) => { setClient(c); if (c) populateBuyerFromClient(c); }}
                      placeholder={t('invoices.editor.clientSearchPlaceholder')}
                    />
                  </div>
                )}
                {client && (
                  <SelectedClient>
                    <span>{t('invoices.editor.selectedClient', { id: client.id })}</span>
                    <LinkButton
                      onClick={() => { setClient(null); setBuyer({ buyer_type: 'private', buyer_name: '', buyer_nip: '', buyer_regon: '', buyer_address: '', buyer_email: '', buyer_phone: '' }); }}
                    >
                      {t('invoices.editor.changeClient')}
                    </LinkButton>
                  </SelectedClient>
                )}

                <RadioGroup>
                  <label>
                    <input type="radio" name="buyer_type" checked={buyer.buyer_type === 'private'} onChange={() => updateBuyer('buyer_type', 'private')} />
                    {t('invoices.editor.buyerType.private')}
                  </label>
                  <label>
                    <input type="radio" name="buyer_type" checked={buyer.buyer_type === 'company'} onChange={() => updateBuyer('buyer_type', 'company')} />
                    {t('invoices.editor.buyerType.company')}
                  </label>
                </RadioGroup>

                <Grid>
                  <Field full>
                    <FieldLabel htmlFor="invoice-buyer-name">
                      {buyer.buyer_type === 'company' ? t('invoices.fields.buyerNameCompany') : t('invoices.fields.buyerNamePrivate')}
                      <RequiredMark>*</RequiredMark>
                    </FieldLabel>
                    <TextInput id="invoice-buyer-name" data-cy="invoice-buyer-name" value={buyer.buyer_name} onChange={(e) => updateBuyer('buyer_name', e.target.value)} />
                  </Field>
                  {buyer.buyer_type === 'company' && (
                    <>
                      <Field full>
                        <FieldLabel htmlFor="invoice-buyer-nip">{t('invoices.fields.nip')}<RequiredMark>*</RequiredMark></FieldLabel>
                        <NipLookupInput
                          id="invoice-buyer-nip"
                          value={buyer.buyer_nip}
                          onChange={(v) => updateBuyer('buyer_nip', v)}
                          onFound={fillBuyerFromRegistry}
                          dataCy="invoice-buyer-nip"
                        />
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="invoice-buyer-regon">{t('invoices.fields.regon')}</FieldLabel>
                        <TextInput id="invoice-buyer-regon" value={buyer.buyer_regon} onChange={(e) => updateBuyer('buyer_regon', e.target.value)} />
                      </Field>
                    </>
                  )}
                  <Field full>
                    <FieldLabel htmlFor="invoice-buyer-address">{t('invoices.fields.address')}</FieldLabel>
                    <TextInput id="invoice-buyer-address" value={buyer.buyer_address} onChange={(e) => updateBuyer('buyer_address', e.target.value)} />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="invoice-buyer-email">{t('invoices.fields.email')}</FieldLabel>
                    <TextInput id="invoice-buyer-email" type="email" value={buyer.buyer_email} onChange={(e) => updateBuyer('buyer_email', e.target.value)} />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="invoice-buyer-phone">{t('invoices.fields.phone')}</FieldLabel>
                    <TextInput id="invoice-buyer-phone" value={buyer.buyer_phone} onChange={(e) => updateBuyer('buyer_phone', e.target.value)} />
                  </Field>
                </Grid>
              </div>
            </PartiesGrid>
          </PanelCard>

          {/* Pozycje */}
          <PanelCard>
            <SectionTitle>{t('invoices.editor.sections.items')}</SectionTitle>
            <TableWrap>
              <ItemsTable>
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>{t('invoices.editor.table.lp')}</th>
                    <th>{t('invoices.editor.table.name')}</th>
                    <th style={{ width: 80 }}>{t('invoices.editor.table.unit')}</th>
                    <th className="num" style={{ width: 90 }}>{t('invoices.editor.table.quantity')}</th>
                    <th className="num" style={{ width: 120 }}>{t(bezVat ? 'invoices.editor.table.unitPrice' : 'invoices.editor.table.unitPriceNet')}</th>
                    <th style={{ width: 112 }}>{t('invoices.editor.table.vat')}</th>
                    {!bezVat && <th className="num" style={{ width: 110 }}>{t('invoices.editor.table.net')}</th>}
                    {!bezVat && <th className="num" style={{ width: 100 }}>{t('invoices.editor.table.vat')}</th>}
                    <th className="num" style={{ width: 110 }}>{t(bezVat ? 'invoices.editor.table.value' : 'invoices.editor.table.gross')}</th>
                    <th style={{ width: 44 }} aria-label={t('invoices.editor.removeItem')} />
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, idx) => {
                    const calc = calcItem(it);
                    return (
                      <tr key={idx}>
                        <td className="lp">{idx + 1}</td>
                        <td>
                          <input
                            type="text"
                            aria-label={t('invoices.editor.table.name')}
                            value={it.name}
                            onChange={(e) => updateItem(idx, 'name', e.target.value)}
                            placeholder={t('invoices.editor.itemNamePlaceholder')}
                          />
                        </td>
                        <td>
                          <input type="text" aria-label={t('invoices.editor.table.unit')} value={it.unit} onChange={(e) => updateItem(idx, 'unit', e.target.value)} />
                        </td>
                        <td className="num">
                          <input type="number" step="0.001" min="0" aria-label={t('invoices.editor.table.quantity')} value={it.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} />
                        </td>
                        <td className="num">
                          <input type="number" step="0.01" aria-label={t('invoices.editor.table.unitPriceNet')} value={it.unit_price_net} onChange={(e) => updateItem(idx, 'unit_price_net', e.target.value)} />
                        </td>
                        <td>
                          <SimpleSelect
                            style={{ width: 100 }}
                            aria-label={t('invoices.editor.table.vat')}
                            data-cy="invoice-item-vat"
                            value={kluczStawki(it)}
                            onChange={(val) => updateRate(idx, val)}
                            disabled={vatExempt && kluczStawki(it) === KOD_ZWOLNIENIA}
                            options={opcjeStawki(it)}
                          />
                        </td>
                        {!bezVat && <td className="num">{money(calc.total_net)}</td>}
                        {!bezVat && <td className="num">{kluczStawki(it) === KOD_ZWOLNIENIA ? t('invoices.editor.notApplicable') : money(calc.total_vat)}</td>}
                        <td className="num" style={{ fontWeight: 600 }}>{money(calc.total_gross)}</td>
                        <td>
                          {items.length > 1 && (
                            <IconButton
                              type="button"
                              $tone="danger"
                              aria-label={t('invoices.editor.removeItem')}
                              onClick={() => removeItem(idx)}
                            >
                              <XIcon size={14} />
                            </IconButton>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </ItemsTable>
            </TableWrap>
            {items.some((it) => cenaBrutto(it) !== null) && (
              <ItemsHint data-cy="invoice-gross-items-hint">{t('invoices.editor.grossItemsHint')}</ItemsHint>
            )}
            {!vatExempt && items.some((it) => kluczStawki(it) === '0') && (
              <ItemsHint data-cy="invoice-zero-rate-hint">{t('invoices.editor.zeroRateHint')}</ItemsHint>
            )}
            {vatExempt && items.some((it) => kluczStawki(it) !== KOD_ZWOLNIENIA) && (
              <Banner tone="warning" compact style={{ marginTop: 'var(--sp-3)' }} data-cy="invoice-exempt-taxed">
                {t('invoices.editor.validation.exemptTaxedItem')}
              </Banner>
            )}
            {maZwolnione && (
              <BasisField>
                <FieldLabel htmlFor="invoice-exemption-basis">{t('invoices.editor.exemptionBasis')}<RequiredMark>*</RequiredMark></FieldLabel>
                <TextInput
                  id="invoice-exemption-basis"
                  data-cy="invoice-exemption-basis"
                  maxLength={MAKS_PODSTAWY}
                  value={exemptionBasis}
                  onChange={(e) => setExemptionBasis(e.target.value)}
                  placeholder={t('invoices.editor.exemptionBasisPh')}
                />
                <ItemsHint>{t('invoices.editor.exemptionBasisHint')}</ItemsHint>
              </BasisField>
            )}
            <ItemsFooter>
              <SecondaryButton type="button" onClick={addItem}>{t('invoices.editor.addItem')}</SecondaryButton>
            </ItemsFooter>

            <NotesField>
              <FieldLabel htmlFor="invoice-notes">{t('invoices.fields.notes')}</FieldLabel>
              <TextArea id="invoice-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('invoices.editor.notesPlaceholder')} />
            </NotesField>
          </PanelCard>
        </Main>

        <Rail>
          <SummaryCard>
            {docTypeLabelKey && <SummaryEyebrow>{t(docTypeLabelKey)}</SummaryEyebrow>}
            <SectionTitle>{t('invoices.editor.summaryTitle')}</SectionTitle>
            {bezVat ? (
              <SummaryRows data-cy="invoice-summary-exempt">
                <dt>{t('invoices.editor.summary.exemptSale')}</dt>
                <dd>{money(totals.total_gross)}</dd>
              </SummaryRows>
            ) : (
              <SummaryRows>
                {totals.breakdown.map(b => (
                  <React.Fragment key={b.key}>
                    <dt>{b.key === KOD_ZWOLNIENIA
                      ? t('invoices.editor.summary.exemptNet')
                      : t('invoices.editor.summary.vatRateNet', { rate: formatPercent(b.rate, { locale: currentLanguage, decimals: 0 }) })}</dt>
                    <dd>{money(b.net)}</dd>
                  </React.Fragment>
                ))}
                <dt>{t('invoices.editor.summary.totalNet')}</dt>
                <dd>{money(totals.total_net)}</dd>
                <dt>{t('invoices.editor.summary.totalVat')}</dt>
                <dd>{money(totals.total_vat)}</dd>
              </SummaryRows>
            )}
            <TotalRow>
              <span>{t(bezVat ? 'invoices.editor.summary.total' : 'invoices.editor.summary.totalGross')}</span>
              <strong data-cy="invoice-total-gross">{money(totals.total_gross)}</strong>
            </TotalRow>

            <RailActions>
              <PrimaryButton data-cy="invoice-save" onClick={() => save(false)} disabled={saving}>
                {saving ? t('common.saving') : issueLabel}
              </PrimaryButton>
              {docType === 'PROFORMA' && (
                <SecondaryButton onClick={() => save(true)} disabled={saving}>
                  {t('invoices.editor.saveDraft')}
                </SecondaryButton>
              )}
              <SecondaryButton onClick={() => navigate('/invoices')} disabled={saving}>{t('common.cancel')}</SecondaryButton>
            </RailActions>
          </SummaryCard>
        </Rail>
      </EditorLayout>
    </Page>
  );
}
